import { gsap } from 'gsap'

/*
 * Movie full-footer "Więcej z sekcji programu" carousel — desktop pin.
 *
 * The module (`movie_full_footer.php`) is a full-bleed horizontal carousel: an
 * intro slide plus the section's other movies. On mobile (and with reduced
 * motion) it is just a native scroll-snap swipe carousel and needs no JS.
 *
 * On desktop with motion this owns the awwwards-style behaviour: pin the whole
 * module once it docks to the viewport top and translate the track horizontally
 * as the page scrolls, unpinning once the last slide is reached. While pinned we
 * hand horizontal scrolling to GSAP — the native overflow scroll (and its
 * keyboard tab-stop) is disabled so the page scroll is the single driver.
 *
 * Mirrors headerMobile.ts / headerNavBrand.ts: an idempotent init that reverts
 * the previous setup, gated to desktop + motion via gsap.matchMedia so it
 * auto-disables on mobile / reduced motion and re-arms on resize. Re-run from
 * the aswap onViewSwapComplete hook (followed by ScrollTrigger.refresh()).
 */

let mm: gsap.MatchMedia | null = null

export const destroyMovieFooter = () => {
  if (mm) {
    mm.revert()
    mm = null
  }
}

export const initMovieFooter = () => {
  destroyMovieFooter()

  const section = document.querySelector<HTMLElement>('[data-movie-footer]')
  if (!section) return
  const viewport = section.querySelector<HTMLElement>('[data-movie-footer-viewport]')
  const track = section.querySelector<HTMLElement>('[data-movie-footer-track]')
  if (!viewport || !track) return

  mm = gsap.matchMedia()
  mm.add('(width >= 62rem) and (prefers-reduced-motion: no-preference)', () => {
    // The native scroll-snap carousel becomes the GSAP-driven one: kill the
    // overflow scroll and drop the now-pointless focusable scroll region.
    const tabindex = viewport.getAttribute('tabindex')
    viewport.style.overflowX = 'hidden'
    viewport.removeAttribute('tabindex')

    // Horizontal distance the track must travel to reveal its last slide.
    const distance = () => Math.max(0, track.scrollWidth - track.clientWidth)

    const tween = gsap.to(track, {
      x: () => -distance(),
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => '+=' + distance(),
        scrub: true,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    })

    return () => {
      tween.scrollTrigger?.kill()
      tween.kill()
      gsap.set(track, { clearProps: 'transform' })
      viewport.style.overflowX = ''
      if (tabindex !== null) {
        viewport.setAttribute('tabindex', tabindex)
      }
    }
  })
}
